import { User } from '@innovation-os-ng/core/models';
import { Observable } from 'rxjs';
import { TokenRefreshedResponse } from '../models/ApiResponse';

/* eslint-disable @typescript-eslint/naming-convention */
export interface JwtToken {
  exp: number;
  sub: string;
  given_name: string; // first name
  family_name: string; // last name
  email: string;
  'custom:DefaultUserUri'?: string; // Old user uri for sso logged-in user
}
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * Provides methods to retrieve information about the authentication
 * state of the current user.
 */
export abstract class SessionService {
  abstract onAuthReady: Observable<string>;
  /**
   * Returns the authentication token of the current user.
   */
  abstract getToken(): string;

  /**
   * Returns the current user's refresh token.
   */
  abstract getRefreshToken(): string;

  /**
   * Returns the current user's access token.
   */
  abstract getAccessToken(): string;

  /**
   * Returns the current user's information.
   */
  abstract getUser(): User;

  /**
   * Returns whether the current token is expired or not.
   */
  abstract isTokenExpired(): boolean;

  /**
   * Returns whether the current user is authorized based on their existing tokens.
   */
  abstract isAuthenticated(): Promise<boolean>;

  /**
   * Saves the authorization state with a given id token, and a refresh token.
   *
   * @param idToken
   * @param refreshToken
   * @param accessToken
   */
  abstract storeAuthentication(idToken: string, refreshToken: string, accessToken: string): void;

  /**
   * Saves the authorization state with a given id token, and a refresh token.
   *
   * @param idToken
   * @param refreshToken
   * @param accessToken
   */
  abstract storeAuthentication(idToken: string, refreshToken: string, accessToken: string): void;

  /**
   * Removes the current authorization state, i.e. logs out the user.
   */
  abstract revokeAuthentication(): void;

  /**
   * Attempts to refresh the current ID token using the current refresh token.
   *
   * @throws InvalidTokenException
   * @throws PasswordResetRequiredException
   * @throws UserNotFoundException
   */
  abstract refreshToken(): Promise<TokenRefreshedResponse>;

  /**
   * Attempts to refresh the current ID token using the current refresh token.
   *
   * @throws InvalidTokenException
   * @throws PasswordResetRequiredException
   * @throws UserNotFoundException
   */
  abstract refreshTokenAsObservable(): Observable<TokenRefreshedResponse>;

  /**
   * Removes the API key Cookie set by the Teaser application.
   */
  abstract removeApiKeyCookie(): void;
}
